import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Column from "../components/Column";
import DatePicker from "../components/DatePicker";
import Icon from "../components/Icon";

import Row from "../components/Row";
import Section from "../components/Section";
import Separator from "../components/Separator";
import Table from "../components/Table";
import { Text, TextTitle } from "../components/Texts";
import campaignAction from "../modules/campaign/actions";
import campaignSelectors from "../modules/campaign/selectors";
import colors from "../styles/colors";
import { compose } from "redux";
import formatMetricValue from "../modules/utils/formatMetricValue";
import mainSelectors from "../modules/main/selectors";
import withUserPerm from "services/withUserPerm";
import { useTranslation } from "react-i18next";
import _mapValues from "lodash/mapValues";
import TopBar from "../components/TopBar";
import styled from "styled-components";
import useWindowSize from "../layout/useWindowSize";
const DisplayCampaigns = () => {
  const DISPLAY_LIMIT = 5;
  const rmpToken = sessionStorage.getItem("rmp_token");
  const currentAd = useSelector((state) => mainSelectors.currentAd(state));
  const [selectedViewValue, setSelectedViewValue] = useState({ value: {} });
  const [startPage, setStartPage] = useState(0);
  const [orderType, setOrderType] = useState({ key: "campaign", direction: "asc" });
  const [orderTopPlacement, setOrderTopPlacement] = useState({ key: "clicks", direction: "desc" });
  const listDisplayCampaignResult = useSelector((state) => campaignSelectors.listDisplayCampaignResult(state));
  const listTopPlacementResult = useSelector((state) => campaignSelectors.listTopPlacementResult(state));
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const onPageChange = (start) => {
    setStartPage(start - 1);
  };

  useEffect(() => {
    dispatch(
      campaignAction.ListDisplayCampaign(
        rmpToken,
        currentAd.ads_account_id,
        selectedViewValue.value.start,
        selectedViewValue.value.end,
        orderType,
        startPage,
        DISPLAY_LIMIT,
      ),
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewValue, startPage, orderType]);

  useEffect(() => {
    dispatch(
      campaignAction.ListTopPlacement(
        rmpToken,
        currentAd.ads_account_id,
        selectedViewValue.value.start,
        selectedViewValue.value.end,
        orderTopPlacement,
      ),
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewValue, orderTopPlacement]);

  return (
    <div>
      <StyledContainerTop useWindowSize={useWindowSize()}>
        <StyledContainerTopBar useWindowSize={useWindowSize()}>
          <TopBar title={t("__display_campaign")} />
        </StyledContainerTopBar>
        <DatePicker onSelected={setSelectedViewValue} />
      </StyledContainerTop>
      <CampaignTable
        data={listDisplayCampaignResult.data.campaign}
        size={DISPLAY_LIMIT}
        total={listDisplayCampaignResult.data.total}
        onPageChange={onPageChange}
        onSortChange={setOrderType}
        orderType={orderType}
      />
      <TopPlacements
        data={listTopPlacementResult.data}
        onSortChange={setOrderTopPlacement}
        orderType={orderTopPlacement}
      />
    </div>
  );
};

export default compose(withUserPerm())(DisplayCampaigns);

const CampaignTable = ({ data = [], size, total, onPageChange, onSortChange, orderType }) => {
  const [t] = useTranslation();
  const tableColumn = [
    {
      key: "campaign",
      title: t("__campaigns"),
      options: {
        width: "160px",
        strongText: true,
        fontSize: "8px",
      },
    },
    {
      key: "cost",
      title: t("__cost"),
      options: {
        align: "right",
      },
    },
    {
      key: "clicks",
      title: t("__clicks"),
      options: {
        align: "right",
      },
    },
    {
      key: "impressions",
      title: t("__impressions"),
      options: {
        align: "right",
      },
    },
    {
      key: "ctr",
      title: t("__ctr"),
      options: {
        align: "right",
      },
    },
    {
      key: "conversions",
      title: t("__conversion"),
      options: {
        align: "right",
      },
    },
    {
      key: "avg_cpc",
      title: t("__avg_cpc"),
      options: {
        align: "right",
      },
    },
  ];
  const pagination = { size, total, onPageChange };
  const sortOptions = {
    ...orderType,
    onSelected: onSortChange,
  };
  data = data.map((item) => {
    return _mapValues(item, (value, key) => {
      if (key === "campaign") {
        return item.isTotal ? (
          value
        ) : (
          <TextTitle size="10px" ellipsis={2} tooltip>
            {value}
          </TextTitle>
        );
      } else {
        return formatMetricValue(key, value);
      }
    });
  });
  return (
    <Section margin="0 0 24px">
      <Table
        columns={tableColumn}
        data={data}
        pagination={pagination}
        responsiveColumns={2}
        sortOptions={sortOptions}
      />
    </Section>
  );
};

const TopPlacements = ({ data = [], onSortChange, orderType }) => {
  const [t] = useTranslation();
  const tableColumn = [
    {
      key: "placement",
      title: t("__placements"),
      options: {
        strongText: true,
        fontSize: "10px",
      },
    },
    {
      key: "campaign",
      title: t("__campaigns"),
      options: {
        fontSize: "10px",
      },
    },
    {
      key: "cost",
      title: t("__cost"),
      options: {
        align: "right",
      },
    },
    {
      key: "clicks",
      title: t("__clicks"),
      options: {
        align: "right",
      },
    },
    {
      key: "impressions",
      title: t("__impressions"),
      options: {
        align: "right",
      },
    },
    {
      key: "ctr",
      title: t("__ctr"),
      options: {
        align: "right",
        width: "60px",
      },
    },
    {
      key: "conversions",
      title: t("__conversion"),
      options: {
        align: "right",
      },
    },
    {
      key: "avg_cpc",
      title: t("__avg_cpc"),
      options: {
        align: "right",
      },
    },
  ];
  const sortOptions = {
    ...orderType,
    onSelected: onSortChange,
  };
  data = data.map((item) => {
    return _mapValues(item, (value, key) => {
      if (key === "campaign") {
        return (
          <Text size="10px" ellipsis={2} tooltip>
            {value}
          </Text>
        );
      } else {
        return key !== "placement" ? formatMetricValue(key, value) : value;
      }
    });
  });
  return (
    <Section margin="0 0 24px">
      <Row alignItems="center">
        <Column wrap>
          <Icon name="icon-award" size="36px" color={colors.primary} />
        </Column>
        <Column>
          <TextTitle size="18px">{t("__top_20_placements")}</TextTitle>
        </Column>
      </Row>
      <Separator />
      <Table columns={tableColumn} data={data} responsiveColumns={1} freezeColumns={2} sortOptions={sortOptions} />
    </Section>
  );
};

const StyledContainerTop = styled.div.attrs((props) => ({
  display: props.useWindowSize.width < 835 ? "block" : "flex",
}))`
  display: ${(props) => props.display};
  justify-content: space-between;
  margin-bottom: 24px;
  display: ;
`;

const StyledContainerTopBar = styled.div.attrs((props) => ({
  display: props.useWindowSize.width < 835 ? "none" : "block",
}))`
  display: ${(props) => props.display};
`;
